/*
 * Turbo Animations — GSAP + ScrollTrigger
 *
 * Animações modernas baseadas em scroll:
 *   - Combinam translate + scale + blur pra um look "premium"
 *   - Disparam quando o elemento está 25% dentro da viewport (perceptível durante o scroll)
 *   - Auto-init via atributo `data-animate="..."`
 *   - Respeita `prefers-reduced-motion`
 *
 * Presets disponíveis (no atributo data-animate):
 *   fade            — opacidade 0 → 1 + blur
 *   fade-up         — sobe + fade + blur + leve scale
 *   fade-down       — desce + fade + blur + leve scale
 *   fade-left       — vem da esquerda + fade + blur
 *   fade-right      — vem da direita + fade + blur
 *   zoom-in         — escala 0.92 → 1 + fade + blur
 *   blur-in         — só desfoque → nítido + fade
 *   reveal-up       — clip-path reveal de baixo pra cima
 *   parallax        — desloca em Y conforme scroll (use data-animate-speed)
 *
 * Modo cascata (anima os filhos diretos em sequência):
 *   data-animate-children="fade-up"           — anima direct children com stagger
 *   data-animate-children-stagger="0.15"      — controla o stagger (default 0.12)
 *
 * Atributos opcionais:
 *   data-animate-delay="0.2"        segundos
 *   data-animate-duration="1.0"     segundos (default 1.1)
 *   data-animate-stagger="0.1"      stagger entre filhos diretos
 *   data-animate-start="top 75%"    trigger start position (ScrollTrigger)
 *   data-animate-speed="0.3"        (apenas parallax) intensidade do deslocamento
 *   data-animate-once="false"       se false, anima toda vez que entra na viewport
 */

(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function waitForLibs(callback, attempt) {
    attempt = attempt || 0;
    if (typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined') {
      callback();
      return;
    }
    if (attempt > 100) return; // ~10s timeout
    setTimeout(function () {
      waitForLibs(callback, attempt + 1);
    }, 100);
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Presets modernos — combinam translate + scale + blur pra um efeito perceptível e premium
  var PRESETS = {
    'fade':       { opacity: 0, filter: 'blur(8px)' },
    'fade-up':    { opacity: 0, y: 60, scale: 0.97, filter: 'blur(8px)' },
    'fade-down':  { opacity: 0, y: -60, scale: 0.97, filter: 'blur(8px)' },
    'fade-left':  { opacity: 0, x: -60, filter: 'blur(8px)' },
    'fade-right': { opacity: 0, x: 60, filter: 'blur(8px)' },
    'zoom-in':    { opacity: 0, scale: 0.92, filter: 'blur(10px)' },
    'blur-in':    { opacity: 0, filter: 'blur(14px)' },
    'reveal-up':  { opacity: 0, clipPath: 'inset(100% 0% 0% 0%)' }
  };

  function parseNumber(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function buildTimeline(el, preset, options) {
    var from = Object.assign({}, PRESETS[preset]);
    var duration = parseNumber(options.duration, 1.1);
    var delay = parseNumber(options.delay, 0);
    var stagger = parseNumber(options.stagger, 0);

    var to = {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      clipPath: 'inset(0% 0% 0% 0%)',
      duration: duration,
      delay: delay,
      ease: 'expo.out'
    };

    var target = stagger > 0 ? el.children : el;
    if (stagger > 0) {
      to.stagger = stagger;
    }

    window.gsap.set(target, from);

    window.ScrollTrigger.create({
      trigger: el,
      // 75% da viewport — quando o usuário ainda está rolando até o elemento,
      // ele vê a animação acontecer
      start: options.start || 'top 75%',
      once: options.once !== 'false',
      onEnter: function () {
        window.gsap.to(target, to);
      },
      onLeaveBack: function () {
        if (options.once === 'false') {
          window.gsap.set(target, from);
        }
      }
    });
  }

  function buildParallax(el, options) {
    var speed = parseNumber(options.speed, 0.3);
    window.gsap.to(el, {
      yPercent: -100 * speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  }

  function buildChildrenCascade(el, preset, options) {
    if (!PRESETS[preset]) return;
    var children = el.children;
    if (!children || children.length === 0) return;

    var from = Object.assign({}, PRESETS[preset]);
    var duration = parseNumber(options.duration, 1.0);
    var delay = parseNumber(options.delay, 0);
    var stagger = parseNumber(options.stagger, 0.12);

    var to = {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      clipPath: 'inset(0% 0% 0% 0%)',
      duration: duration,
      delay: delay,
      stagger: stagger,
      ease: 'expo.out'
    };

    window.gsap.set(children, from);

    window.ScrollTrigger.create({
      trigger: el,
      start: options.start || 'top 78%',
      once: options.once !== 'false',
      onEnter: function () {
        window.gsap.to(children, to);
      },
      onLeaveBack: function () {
        if (options.once === 'false') {
          window.gsap.set(children, from);
        }
      }
    });
  }

  function initAnimations(root) {
    root = root || document;

    // Cascata: anima os filhos diretos de um wrapper em sequência
    var cascades = root.querySelectorAll('[data-animate-children]:not([data-animate-init])');
    cascades.forEach(function (el) {
      el.setAttribute('data-animate-init', 'true');
      var preset = el.getAttribute('data-animate-children');
      var options = {
        delay: el.getAttribute('data-animate-delay'),
        duration: el.getAttribute('data-animate-duration'),
        stagger: el.getAttribute('data-animate-children-stagger'),
        start: el.getAttribute('data-animate-start'),
        once: el.getAttribute('data-animate-once')
      };
      buildChildrenCascade(el, preset, options);
    });

    // Elemento individual
    var elements = root.querySelectorAll('[data-animate]:not([data-animate-init])');
    elements.forEach(function (el) {
      el.setAttribute('data-animate-init', 'true');
      var preset = el.getAttribute('data-animate');
      var options = {
        delay: el.getAttribute('data-animate-delay'),
        duration: el.getAttribute('data-animate-duration'),
        stagger: el.getAttribute('data-animate-stagger'),
        start: el.getAttribute('data-animate-start'),
        speed: el.getAttribute('data-animate-speed'),
        once: el.getAttribute('data-animate-once')
      };

      if (preset === 'parallax') {
        buildParallax(el, options);
      } else if (PRESETS[preset]) {
        buildTimeline(el, preset, options);
      }
    });
  }

  ready(function () {
    if (prefersReducedMotion()) {
      // Acessibilidade: mostra tudo sem animação
      var resetAll = function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.filter = 'none';
        el.style.clipPath = 'none';
      };
      document.querySelectorAll('[data-animate]').forEach(resetAll);
      document.querySelectorAll('[data-animate-children]').forEach(function (el) {
        Array.prototype.forEach.call(el.children, resetAll);
      });
      return;
    }

    waitForLibs(function () {
      window.gsap.registerPlugin(window.ScrollTrigger);
      initAnimations(document);

      // Re-init quando seções são adicionadas/recarregadas no Theme Editor
      document.addEventListener('shopify:section:load', function (e) {
        initAnimations(e.target);
        window.ScrollTrigger.refresh();
      });

      document.addEventListener('shopify:section:unload', function () {
        window.ScrollTrigger.refresh();
      });
    });
  });

  // Expor pra uso ad-hoc em outras sections
  window.tpAnimations = {
    initAnimations: initAnimations
  };
})();
